import { extractErrorMessage } from "../utils/acceptSuggestions";
import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { toast } from "sonner";
import {
  Target,
  Users,
  DollarSign,
  Compass,
  CheckCircle2,
  ChevronRight,
  ChevronLeft,
  AlertTriangle,
  FileText,
  Plus,
  Trash2,
  RefreshCw,
  Shield,
  Eye,
  Download,
  Sparkles,
  Clock,
  User,
  Mail,
  Phone,
  Building2,
  Scale,
  Landmark,
  TrendingUp,
  Briefcase,
} from "lucide-react";

const ROLE_TYPE_OPTIONS = [
  { value: "emergency_declaration", label: "Emergency Declaration Party", icon: AlertTriangle, color: "text-red-600" },
  { value: "emt", label: "Emergency Management Team (EMT)", icon: Users, color: "text-sky-700" },
  { value: "imt", label: "Incident Management Team (IMT)", icon: Shield, color: "text-blue-600" },
];

const ROLE_STATUS_OPTIONS = [
  { value: "unassigned", label: "Unassigned", color: "bg-slate-100 text-slate-700" },
  { value: "pending", label: "Pending", color: "bg-amber-100 text-amber-700" },
  { value: "active", label: "Active", color: "bg-emerald-100 text-emerald-700" },
];

const SENSITIVITY_CATEGORIES = [
  { id: "financial", name: "Financial", icon: DollarSign, description: "Revenue loss, cash flow impact, market value" },
  { id: "legal", name: "Legal", icon: Scale, description: "Contractual obligations, litigation risk, liability" },
  { id: "regulatory", name: "Regulatory", icon: Landmark, description: "Compliance requirements, regulatory penalties" },
  { id: "reputational", name: "Reputational", icon: TrendingUp, description: "Brand damage, customer trust, stakeholder confidence" },
  { id: "operational", name: "Operational", icon: Briefcase, description: "Process disruption, supply chain, service delivery" },
];

const TIME_BANDS = [
  { id: "band_1", name: "0-4 Hours", label: "Critical/Immediate" },
  { id: "band_2", name: "4-24 Hours", label: "Urgent/Same Day" },
  { id: "band_3", name: "24-72 Hours", label: "Operational/Multi-day" },
  { id: "band_4", name: "72+ Hours / 1 Week", label: "Strategic/Long-term" },
];

const WIZARD_STEPS = [
  { id: 0, title: "Mandate", description: "Mission, Vision & Objectives", icon: Target },
  { id: 1, title: "Authority Matrix", description: "Roles & Responsibilities", icon: Users },
  { id: 2, title: "Impact Matrix", description: "Financial Thresholds", icon: DollarSign },
  { id: 3, title: "Sensitivity Anchor", description: "Primary RTO Driver", icon: Compass },
];

export default function GovernancePage() {
  const { user, api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [governance, setGovernance] = useState(null);
  const [roleTemplates, setRoleTemplates] = useState([]);
  const [showPolicyPreview, setShowPolicyPreview] = useState(false);
  const [regeneratingPolicy, setRegeneratingPolicy] = useState(false);

  // Form states
  const [mandate, setMandate] = useState({
    mission: "",
    vision: "",
    objectives: [""],
    scope: "",
    exclusions: [],
  });

  const [authorityMatrix, setAuthorityMatrix] = useState({
    roles: [],
  });

  const [impactMatrix, setImpactMatrix] = useState({
    revenue_loss: { band_1: 0, band_2: 0, band_3: 0, band_4: 0 },
    recovery_cost: { band_1: 0, band_2: 0, band_3: 0, band_4: 0 },
    regulatory_penalty: { band_1: 0, band_2: 0, band_3: 0, band_4: 0 },
    reputational_damage: { band_1: 0, band_2: 0, band_3: 0, band_4: 0 },
    operational_impact: { band_1: 0, band_2: 0, band_3: 0, band_4: 0 },
  });

  const [sensitivityAnchor, setSensitivityAnchor] = useState({
    primary_category: "",
    justification: "",
  });

  const isBCMAdmin = user?.role === "bcm_manager" || user?.role === "admin";

  const fetchGovernance = useCallback(async () => {
    try {
      const response = await api.get("/bia-engine/governance");
      setGovernance(response.data);

      // Populate form states from existing data
      if (response.data.mandate) {
        setMandate({
          mission: response.data.mandate.mission || "",
          vision: response.data.mandate.vision || "",
          objectives: response.data.mandate.objectives?.length > 0 ? response.data.mandate.objectives : [""],
          scope: response.data.mandate.scope || "",
          exclusions: response.data.mandate.exclusions || [],
        });
      }

      if (response.data.authority_matrix) {
        setAuthorityMatrix({
          roles: response.data.authority_matrix.roles || [],
        });
      }

      if (response.data.impact_matrix) {
        setImpactMatrix({
          revenue_loss: response.data.impact_matrix.revenue_loss || { band_1: 0, band_2: 0, band_3: 0, band_4: 0 },
          recovery_cost: response.data.impact_matrix.recovery_cost || { band_1: 0, band_2: 0, band_3: 0, band_4: 0 },
          regulatory_penalty: response.data.impact_matrix.regulatory_penalty || { band_1: 0, band_2: 0, band_3: 0, band_4: 0 },
          reputational_damage: response.data.impact_matrix.reputational_damage || { band_1: 0, band_2: 0, band_3: 0, band_4: 0 },
          operational_impact: response.data.impact_matrix.operational_impact || { band_1: 0, band_2: 0, band_3: 0, band_4: 0 },
        });
      }

      if (response.data.sensitivity_anchor) {
        setSensitivityAnchor({
          primary_category: response.data.sensitivity_anchor.primary_category || "",
          justification: response.data.sensitivity_anchor.justification || "",
        });
      }
    } catch (error) {
      console.error("Failed to fetch governance:", error);
    } finally {
      setLoading(false);
    }
  }, [api]);

  const fetchRoleTemplates = useCallback(async () => {
    try {
      const response = await api.get("/bia-engine/role-templates");
      setRoleTemplates(response.data.role_templates || []);
    } catch (error) {
      console.error("Failed to fetch role templates:", error);
    }
  }, [api]);

  useEffect(() => {
    fetchGovernance();
    fetchRoleTemplates();
  }, [fetchGovernance, fetchRoleTemplates]);

  // Mandate handlers
  const addObjective = () => {
    setMandate({ ...mandate, objectives: [...mandate.objectives, ""] });
  };

  const updateObjective = (index, value) => {
    const newObjectives = [...mandate.objectives];
    newObjectives[index] = value;
    setMandate({ ...mandate, objectives: newObjectives });
  };

  const removeObjective = (index) => {
    if (mandate.objectives.length > 1) {
      const newObjectives = mandate.objectives.filter((_, i) => i !== index);
      setMandate({ ...mandate, objectives: newObjectives });
    }
  };

  const saveMandate = async () => {
    if (!mandate.mission || !mandate.vision || mandate.objectives.filter(o => o.trim()).length === 0) {
      toast.error("Please fill in Mission, Vision, and at least one Objective");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...mandate,
        objectives: mandate.objectives.filter(o => o.trim()),
      };
      await api.post("/bia-engine/governance/mandate", payload);
      toast.success("Mandate saved successfully!");
      fetchGovernance();
    } catch (error) {
      toast.error(extractErrorMessage(error, "Failed to save mandate"));
    } finally {
      setSaving(false);
    }
  };

  // Authority Matrix handlers
  const addRole = (template = null) => {
    const newRole = template ? {
      ...template,
      id: null,
      person_name: "",
      email: "",
      phone: "",
      status: "unassigned",
      backup_person: "",
    } : {
      id: null,
      role_name: "",
      role_type: "imt",
      person_name: "",
      email: "",
      phone: "",
      status: "unassigned",
      escalation_level: 3,
      responsibilities: [],
      backup_person: "",
    };
    setAuthorityMatrix({ ...authorityMatrix, roles: [...authorityMatrix.roles, newRole] });
  };

  const updateRole = (index, field, value) => {
    const newRoles = [...authorityMatrix.roles];
    newRoles[index] = { ...newRoles[index], [field]: value };
    setAuthorityMatrix({ ...authorityMatrix, roles: newRoles });
  };

  const removeRole = (index) => {
    const newRoles = authorityMatrix.roles.filter((_, i) => i !== index);
    setAuthorityMatrix({ ...authorityMatrix, roles: newRoles });
  };

  const saveAuthorityMatrix = async () => {
    if (authorityMatrix.roles.length === 0) {
      toast.error("Please add at least one role");
      return;
    }

    const invalidRoles = authorityMatrix.roles.filter(r => !r.role_name);
    if (invalidRoles.length > 0) {
      toast.error("All roles must have a name");
      return;
    }

    setSaving(true);
    try {
      await api.post("/bia-engine/governance/authority-matrix", authorityMatrix);
      toast.success("Authority Matrix saved successfully!");
      fetchGovernance();
    } catch (error) {
      toast.error(extractErrorMessage(error, "Failed to save authority matrix"));
    } finally {
      setSaving(false);
    }
  };

  // Impact Matrix handlers
  const updateImpactValue = (category, band, value) => {
    setImpactMatrix({
      ...impactMatrix,
      [category]: {
        ...impactMatrix[category],
        [band]: parseFloat(value) || 0,
      },
    });
  };

  const saveImpactMatrix = async () => {
    setSaving(true);
    try {
      await api.post("/bia-engine/governance/impact-matrix", impactMatrix);
      toast.success("Impact Matrix saved successfully!");
      fetchGovernance();
    } catch (error) {
      toast.error(extractErrorMessage(error, "Failed to save impact matrix"));
    } finally {
      setSaving(false);
    }
  };

  // Sensitivity Anchor handlers
  const saveSensitivityAnchor = async () => {
    if (!sensitivityAnchor.primary_category) {
      toast.error("Please select a primary category");
      return;
    }

    setSaving(true);
    try {
      await api.post("/bia-engine/governance/sensitivity-anchor", sensitivityAnchor);
      toast.success("Sensitivity Anchor saved successfully!");
      fetchGovernance();
    } catch (error) {
      toast.error(extractErrorMessage(error, "Failed to save sensitivity anchor"));
    } finally {
      setSaving(false);
    }
  };

  // Policy regeneration
  const regeneratePolicy = async () => {
    setRegeneratingPolicy(true);
    try {
      await api.post("/bia-engine/governance/regenerate-policy");
      toast.success("BCM Policy regenerated!");
      fetchGovernance();
    } catch (error) {
      toast.error(extractErrorMessage(error, "Failed to regenerate policy"));
    } finally {
      setRegeneratingPolicy(false);
    }
  };

  if (!isBCMAdmin) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]" data-testid="governance-access-denied">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <Shield className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-900 mb-2">Access Restricted</h2>
            <p className="text-slate-600 mb-4">
              BCM Governance configuration is only accessible to BCM Manageristrators.
            </p>
            <Button onClick={() => window.history.back()}>Go Back</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="governance-loading">
        <div className="w-8 h-8 border-4 border-sky-700 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const completionStatus = governance?.completion_status || {};
  const completionPercentage = Object.values(completionStatus).filter(Boolean).length * 25;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="governance-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">BCM Governance</h1>
          <p className="text-slate-600 mt-1">
            Configure your organization&apos;s Business Continuity Management foundation
          </p>
        </div>
        <div className="flex items-center gap-3">
          {governance?.bcm_policy_draft && (
            <Button
              variant="outline"
              onClick={() => setShowPolicyPreview(true)}
              data-testid="view-policy-btn"
            >
              <Eye className="w-4 h-4 mr-2" />
              View BCM Policy
            </Button>
          )}
          <div className="flex items-center gap-2 px-4 py-2 bg-sky-50 rounded-lg">
            <span className="text-sm text-sky-700 font-medium">{completionPercentage}% Complete</span>
            <div className="w-24 h-2 bg-sky-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-sky-700 transition-all duration-500"
                style={{ width: `${completionPercentage}%` }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Gap Alerts */}
      {governance?.gap_alerts?.length > 0 && (
        <Card className="border-red-200 bg-red-50" data-testid="gap-alerts-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg text-red-800 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5" />
              Gap Alerts ({governance.gap_alerts.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {governance.gap_alerts.map((alert, idx) => (
                <div
                  key={idx}
                  className={`p-3 rounded-lg border ${
                    alert.severity === "critical"
                      ? "bg-red-100 border-red-300"
                      : "bg-amber-100 border-amber-300"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Badge variant={alert.severity === "critical" ? "destructive" : "warning"}>
                        {alert.severity.toUpperCase()}
                      </Badge>
                      <span className="font-medium">{alert.role_name}</span>
                    </div>
                    <span className="text-sm text-slate-600">{alert.current_status}</span>
                  </div>
                  <p className="text-sm mt-1">{alert.message}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step Navigation — TurboTax Progressive Disclosure Style */}
      <div className="bg-white rounded-xl p-5 shadow-sm border">
        {/* Progress bar */}
        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden mb-4">
          <div
            className="h-full rounded-full bg-gradient-to-r from-sky-600 to-teal-500 transition-all duration-500 ease-out"
            style={{ width: `${Math.max(((currentStep + 1) / WIZARD_STEPS.length) * 100, 8)}%` }}
          />
        </div>
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs font-semibold text-slate-400">Step {currentStep + 1} of {WIZARD_STEPS.length}</span>
          <span className="text-xs text-slate-400">{completionPercentage}% Complete</span>
        </div>
        {/* Step pills */}
        <div className="flex items-center gap-1 overflow-x-auto pb-1">
          {WIZARD_STEPS.map((step, index) => {
            const isActive = currentStep === step.id;
            const isCompleted = completionStatus[step.title.toLowerCase().replace(" ", "_")] ||
              (step.id === 0 && completionStatus.mandate) ||
              (step.id === 1 && completionStatus.authority_matrix) ||
              (step.id === 2 && completionStatus.impact_matrix) ||
              (step.id === 3 && completionStatus.sensitivity_anchor);
            const Icon = step.icon;

            return (
              <button
                key={step.id}
                onClick={() => setCurrentStep(step.id)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-all ${
                  isActive
                    ? "bg-sky-700 text-white shadow-sm"
                    : isCompleted
                    ? "bg-teal-50 text-teal-700 hover:bg-teal-100"
                    : "bg-slate-50 text-slate-500 hover:bg-slate-100"
                }`}
                data-testid={`wizard-step-${step.id}`}
              >
                {isCompleted && !isActive ? (
                  <CheckCircle2 className="w-3 h-3" />
                ) : (
                  <Icon className="w-3 h-3" />
                )}
                {step.title}
              </button>
            );
          })}
        </div>
      </div>

      {/* Step 1: Mandate */}
      {currentStep === 0 && (
        <Card data-testid="mandate-step">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Target className="w-5 h-5 text-sky-700" />
              Organizational Mandate
            </CardTitle>
            <CardDescription>
              Define your organization&apos;s Mission, Vision, and BCM Objectives to draft the BCM Policy
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label className="text-base font-semibold">Mission Statement *</Label>
              <Textarea
                placeholder="What is your organization's core purpose and reason for existence?"
                value={mandate.mission}
                onChange={(e) => setMandate({ ...mandate, mission: e.target.value })}
                rows={3}
                className="resize-none"
                data-testid="mandate-mission"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-base font-semibold">Vision Statement *</Label>
              <Textarea
                placeholder="What does your organization aspire to become?"
                value={mandate.vision}
                onChange={(e) => setMandate({ ...mandate, vision: e.target.value })}
                rows={3}
                className="resize-none"
                data-testid="mandate-vision"
              />
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-base font-semibold">BCM Objectives *</Label>
                <Button variant="outline" size="sm" onClick={addObjective}>
                  <Plus className="w-4 h-4 mr-1" />
                  Add Objective
                </Button>
              </div>
              {mandate.objectives.map((obj, index) => (
                <div key={index} className="flex items-center gap-2">
                  <span className="text-sm text-slate-500 w-6">{index + 1}.</span>
                  <Input
                    placeholder="e.g., Minimize disruption to critical business operations"
                    value={obj}
                    onChange={(e) => updateObjective(index, e.target.value)}
                    data-testid={`mandate-objective-${index}`}
                  />
                  {mandate.objectives.length > 1 && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeObjective(index)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>

            <div className="space-y-2">
              <Label className="text-base font-semibold">Scope</Label>
              <Textarea
                placeholder="Define the boundaries and applicability of the BCM program..."
                value={mandate.scope}
                onChange={(e) => setMandate({ ...mandate, scope: e.target.value })}
                rows={2}
                className="resize-none"
                data-testid="mandate-scope"
              />
            </div>

            <div className="flex justify-between pt-4">
              <div />
              <div className="flex gap-3">
                <Button onClick={saveMandate} disabled={saving} className="bg-sky-700 hover:bg-sky-800">
                  {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                  Save & Generate Policy
                </Button>
                <Button variant="outline" onClick={() => setCurrentStep(1)}>
                  Next <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 2: Authority Matrix */}
      {currentStep === 1 && (
        <Card data-testid="authority-matrix-step">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-sky-700" />
              Authority Matrix
            </CardTitle>
            <CardDescription>
              Define Emergency Declaration Party, EMT, and IMT roles. Critical roles will generate Gap Alerts if unassigned.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Role Templates */}
            <div className="p-4 bg-slate-50 rounded-lg border">
              <h4 className="font-semibold mb-3">Quick Add from Templates</h4>
              <div className="flex flex-wrap gap-2">
                {roleTemplates.map((group) => (
                  group.templates.map((template, idx) => (
                    <Button
                      key={`${group.role_type}-${idx}`}
                      variant="outline"
                      size="sm"
                      onClick={() => addRole({ ...template, role_type: group.role_type })}
                    >
                      <Plus className="w-3 h-3 mr-1" />
                      {template.role_name}
                    </Button>
                  ))
                ))}
              </div>
            </div>

            {/* Roles List */}
            <div className="space-y-4">
              {authorityMatrix.roles.map((role, index) => {
                const roleTypeConfig = ROLE_TYPE_OPTIONS.find(r => r.value === role.role_type);
                const RoleIcon = roleTypeConfig?.icon || Users;

                return (
                  <div
                    key={index}
                    className="p-4 border rounded-lg bg-white hover:shadow-sm transition-shadow"
                    data-testid={`role-card-${index}`}
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-2">
                        <RoleIcon className={`w-5 h-5 ${roleTypeConfig?.color || "text-slate-600"}`} />
                        <Select
                          value={role.role_type}
                          onValueChange={(value) => updateRole(index, "role_type", value)}
                        >
                          <SelectTrigger className="w-64">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {ROLE_TYPE_OPTIONS.map((opt) => (
                              <SelectItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeRole(index)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                      <div className="space-y-1">
                        <Label className="text-xs text-slate-500">Role Name *</Label>
                        <Input
                          placeholder="e.g., Crisis Director"
                          value={role.role_name}
                          onChange={(e) => updateRole(index, "role_name", e.target.value)}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-slate-500">Assigned Person</Label>
                        <div className="relative">
                          <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                          <Input
                            className="pl-9"
                            placeholder="Person name"
                            value={role.person_name || ""}
                            onChange={(e) => updateRole(index, "person_name", e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-slate-500">Status</Label>
                        <Select
                          value={role.status}
                          onValueChange={(value) => updateRole(index, "status", value)}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {ROLE_STATUS_OPTIONS.map((opt) => (
                              <SelectItem key={opt.value} value={opt.value}>
                                <div className="flex items-center gap-2">
                                  <span className={`w-2 h-2 rounded-full ${opt.color.split(" ")[0]}`} />
                                  {opt.label}
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-slate-500">Email</Label>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                          <Input
                            className="pl-9"
                            type="email"
                            placeholder="email@company.com"
                            value={role.email || ""}
                            onChange={(e) => updateRole(index, "email", e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-slate-500">Phone</Label>
                        <div className="relative">
                          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                          <Input
                            className="pl-9"
                            placeholder="+1 (555) 000-0000"
                            value={role.phone || ""}
                            onChange={(e) => updateRole(index, "phone", e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs text-slate-500">Escalation Level (1-5)</Label>
                        <Select
                          value={String(role.escalation_level)}
                          onValueChange={(value) => updateRole(index, "escalation_level", parseInt(value))}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {[1, 2, 3, 4, 5].map((level) => (
                              <SelectItem key={level} value={String(level)}>
                                Level {level} {level === 5 ? "(Critical)" : level === 1 ? "(Low)" : ""}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                );
              })}

              {authorityMatrix.roles.length === 0 && (
                <div className="text-center py-12 bg-slate-50 rounded-lg border-2 border-dashed">
                  <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-500 mb-3">No roles defined yet</p>
                  <Button variant="outline" onClick={() => addRole()}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Custom Role
                  </Button>
                </div>
              )}
            </div>

            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={() => setCurrentStep(0)}>
                <ChevronLeft className="w-4 h-4 mr-1" /> Previous
              </Button>
              <div className="flex gap-3">
                <Button onClick={() => addRole()} variant="outline">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Custom Role
                </Button>
                <Button onClick={saveAuthorityMatrix} disabled={saving} className="bg-sky-700 hover:bg-sky-800">
                  {saving && <RefreshCw className="w-4 h-4 mr-2 animate-spin" />}
                  Save Authority Matrix
                </Button>
                <Button variant="outline" onClick={() => setCurrentStep(2)}>
                  Next <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 3: Impact Over Time Matrix */}
      {currentStep === 2 && (
        <Card data-testid="impact-matrix-step">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-sky-700" />
              Impact Over Time Matrix
            </CardTitle>
            <CardDescription>
              Define financial thresholds for each time band. These values drive RTO calculations.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Time Bands Header */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr>
                    <th className="text-left p-3 bg-slate-50 rounded-tl-lg">Impact Category</th>
                    {TIME_BANDS.map((band) => (
                      <th key={band.id} className="p-3 bg-slate-50 text-center">
                        <div className="flex flex-col items-center">
                          <Clock className="w-4 h-4 text-slate-400 mb-1" />
                          <span className="font-semibold text-sm">{band.name}</span>
                          <span className="text-xs text-slate-500">{band.label}</span>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {[
                    { key: "revenue_loss", label: "Revenue Loss ($)", icon: DollarSign },
                    { key: "recovery_cost", label: "Recovery Cost ($)", icon: TrendingUp },
                    { key: "regulatory_penalty", label: "Regulatory Penalty ($)", icon: Landmark },
                    { key: "reputational_damage", label: "Reputational Damage ($)", icon: Building2 },
                    { key: "operational_impact", label: "Operational Impact ($)", icon: Briefcase },
                  ].map((category) => {
                    const Icon = category.icon;
                    return (
                      <tr key={category.key} className="border-b">
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            <Icon className="w-4 h-4 text-slate-400" />
                            <span className="font-medium">{category.label}</span>
                          </div>
                        </td>
                        {TIME_BANDS.map((band) => (
                          <td key={band.id} className="p-2">
                            <Input
                              type="number"
                              min="0"
                              placeholder="0"
                              value={impactMatrix[category.key]?.[band.id] || ""}
                              onChange={(e) => updateImpactValue(category.key, band.id, e.target.value)}
                              className="text-right"
                              data-testid={`impact-${category.key}-${band.id}`}
                            />
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-800">
                <strong>Tip:</strong> Higher values in earlier time bands indicate more critical processes. 
                The BIA Engine will use these thresholds to automatically calculate RTO when a process 
                reports financial impact in that range.
              </p>
            </div>

            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={() => setCurrentStep(1)}>
                <ChevronLeft className="w-4 h-4 mr-1" /> Previous
              </Button>
              <div className="flex gap-3">
                <Button onClick={saveImpactMatrix} disabled={saving} className="bg-sky-700 hover:bg-sky-800">
                  {saving && <RefreshCw className="w-4 h-4 mr-2 animate-spin" />}
                  Save Impact Matrix
                </Button>
                <Button variant="outline" onClick={() => setCurrentStep(3)}>
                  Next <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 4: Sensitivity Anchor */}
      {currentStep === 3 && (
        <Card data-testid="sensitivity-anchor-step">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Compass className="w-5 h-5 text-sky-700" />
              Sensitivity Anchor
            </CardTitle>
            <CardDescription>
              Select the primary category that drives RTO decisions. This becomes the &quot;anchor&quot; for all BIA calculations.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {SENSITIVITY_CATEGORIES.map((category) => {
                const Icon = category.icon;
                const isSelected = sensitivityAnchor.primary_category === category.id;

                return (
                  <button
                    key={category.id}
                    onClick={() => setSensitivityAnchor({ ...sensitivityAnchor, primary_category: category.id })}
                    className={`p-4 rounded-xl border-2 text-left transition-all ${
                      isSelected
                        ? "border-sky-700 bg-sky-50"
                        : "border-slate-200 hover:border-slate-300 bg-white"
                    }`}
                    data-testid={`sensitivity-${category.id}`}
                  >
                    <div className="flex items-center gap-3 mb-2">
                      <div
                        className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                          isSelected ? "bg-sky-700 text-white" : "bg-slate-100 text-slate-600"
                        }`}
                      >
                        <Icon className="w-5 h-5" />
                      </div>
                      <div>
                        <h4 className="font-semibold">{category.name}</h4>
                        {isSelected && (
                          <Badge className="bg-sky-700 mt-1">Primary Anchor</Badge>
                        )}
                      </div>
                    </div>
                    <p className="text-sm text-slate-500">{category.description}</p>
                  </button>
                );
              })}
            </div>

            {sensitivityAnchor.primary_category && (
              <div className="space-y-2">
                <Label className="text-base font-semibold">Justification (Optional)</Label>
                <Textarea
                  placeholder="Explain why this category is the primary driver for your organization&apos;s RTO decisions..."
                  value={sensitivityAnchor.justification}
                  onChange={(e) => setSensitivityAnchor({ ...sensitivityAnchor, justification: e.target.value })}
                  rows={3}
                  className="resize-none"
                  data-testid="sensitivity-justification"
                />
              </div>
            )}

            <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-sm text-amber-800">
                <strong>Important:</strong> The Sensitivity Anchor determines which category takes priority 
                when calculating RTO. If multiple categories reach &quot;High&quot; impact simultaneously, the anchor 
                category&apos;s time band will be used for the final RTO.
              </p>
            </div>

            <div className="flex justify-between pt-4">
              <Button variant="outline" onClick={() => setCurrentStep(2)}>
                <ChevronLeft className="w-4 h-4 mr-1" /> Previous
              </Button>
              <Button onClick={saveSensitivityAnchor} disabled={saving} className="bg-sky-700 hover:bg-sky-800">
                {saving && <RefreshCw className="w-4 h-4 mr-2 animate-spin" />}
                Save & Complete Governance
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* BCM Policy Preview Dialog */}
      <Dialog open={showPolicyPreview} onOpenChange={setShowPolicyPreview}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-sky-700" />
              BCM Policy Draft
            </DialogTitle>
            <DialogDescription>
              AI-generated policy based on your organizational mandate
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4">
            <div className="flex justify-end gap-2 mb-4">
              <Button
                variant="outline"
                onClick={regeneratePolicy}
                disabled={regeneratingPolicy}
              >
                {regeneratingPolicy ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Sparkles className="w-4 h-4 mr-2" />
                )}
                Regenerate
              </Button>
              <Button variant="outline">
                <Download className="w-4 h-4 mr-2" />
                Export
              </Button>
            </div>
            <div className="prose prose-sm max-w-none p-6 bg-slate-50 rounded-lg border whitespace-pre-wrap">
              {governance?.bcm_policy_draft || "No policy generated yet. Complete the Mandate step first."}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
